import { useId, useRef, useState } from "react";
import { Form, Link, redirect, useActionData, useNavigation } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useSuspenseQueries } from "@tanstack/react-query";
import type { EventOptions, ResultScore, ScoreTypeOption } from "@repo/shared-types";
import { api } from "@/lib/api";
import { requireSessionWith } from "@/lib/guards";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { errorMessage } from "@/lib/session";
import { SHEET_PREFIX, formOf, partLabel, partSummary, type PartFields } from "@/lib/event-form";
import { readScore, scoreDisplay } from "@/lib/measures";
import { LEVELS, feelEmoji, levelLabel } from "@/lib/results";
import { toWallTime, viewerZone } from "@/lib/times";
import { readLoggedParts, readWorkout } from "@/lib/workout-form";
import { ErrorNote, SegmentedControl, covers, useFormProblem } from "@/components/form";
import { DraftParts, type DraftPart } from "@/components/event-fields";
import { LeaveGuard } from "@/components/leave-guard";
import { NoteFields, RxPlusToggle, ScoreBoxes } from "@/components/result-fields";
import { BackLink } from "@/components/navigation";
import { PageTitle } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";

/**
 * Logging a workout of your own, from /t/me: what it was — a title, a sport
 * and a format, its parts and how each is scored — and how it went, in one
 * form. It is never shared; it lands in the log, and on a page of its own.
 *
 * What it was cannot change afterwards, like an event's copy: its results,
 * levels, when it was done, notes and feels can, from its page. Its division
 * is the profile's, copied in by the api, as for any result.
 */

/** A part's level as logged: Rx, Scaled, or "" for none, which leaves it without levels. */
type Level = (typeof LEVELS)[number]["value"] | "";

export async function newWorkoutLoader({ request }: LoaderFunctionArgs) {
  await requireSessionWith(request, () => Promise.all([read(queries.eventOptions()), read(queries.profile())]));
  return null;
}

export async function newWorkoutAction({ request }: ActionFunctionArgs) {
  const form = await request.formData();
  try {
    const [profile, options] = await Promise.all([api.me.profile.get(), api.eventOptions()]);
    const read = readWorkout(form, viewerZone(profile.timeZone).zone, options);
    if (read.error !== undefined) return { error: read.error, field: read.field };
    const entry = await api.entries.log(read.value);
    return redirect(`/t/me/entries/${entry.id}`);
  } catch (error) {
    return { error: errorMessage(error, "Could not log the workout") };
  }
}

export default function NewWorkout() {
  // The form's boxes are uncontrolled and its parts state, so a fresh answer
  // redraws what is around them and leaves what was typed where it is.
  const [{ data: options }, { data: profile }] = useSuspenseQueries({
    queries: [queries.eventOptions(), queries.profile()],
  });
  const { zone } = viewerZone(profile.timeZone);
  const result = useActionData() as { error?: string; field?: string } | undefined;
  const navigation = useNavigation();
  // None to start with, and nothing to log until there is one (decided with
  // Tiago 20 October): each is made in its sheet, as an event's part is.
  const [parts, setParts] = useState<DraftPart[]>([]);
  const form = useRef<HTMLFormElement>(null);
  const shown = useFormProblem(result, form);
  const problem = (name: string) => (shown.problem && covers(shown.problem.field, name) ? shown.problem.error : undefined);
  // Now, read once: a box that moved with the clock would read as typed in.
  const [now] = useState(() => toWallTime(new Date().toISOString(), zone));
  const id = useId();

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <BackLink to="/t/me">Your log</BackLink>
        <PageTitle of={["Log a workout"]} />
        <h1 className="page-title">Log a workout</h1>
        <p className="text-muted-foreground">
          One of your own — a past workout, a replica of a competition. It stays in your log and is never shared.
        </p>
      </div>

      <Form ref={form} method="post" className="space-y-6" noValidate onChange={shown.onChange}>
        {shown.general && <ErrorNote message={shown.general} />}
        <Card>
          <CardContent>
            <FieldGroup className="gap-5">
              <Field className="gap-2" data-invalid={problem("title") ? true : undefined}>
                <FieldLabel htmlFor={`${id}-title`}>Title</FieldLabel>
                <Input
                  id={`${id}-title`}
                  name="title"
                  required
                  maxLength={120}
                  aria-invalid={problem("title") ? true : undefined}
                />
                {problem("title") && <FieldError>{problem("title")}</FieldError>}
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field className="gap-2">
                  <FieldLabel htmlFor={`${id}-sport`}>Sport</FieldLabel>
                  <NativeSelect id={`${id}-sport`} name="sportId" defaultValue={options.sports[0]?.id} className="w-full">
                    {options.sports.map((sport) => (
                      <NativeSelectOption key={sport.id} value={sport.id}>
                        {sport.label}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </Field>
                <Field className="gap-2">
                  <FieldLabel htmlFor={`${id}-format`}>Format</FieldLabel>
                  <NativeSelect id={`${id}-format`} name="formatId" defaultValue={options.formats[0]?.id} className="w-full">
                    {options.formats.filter((format) => !format.isPost).map((format) => (
                      <NativeSelectOption key={format.id} value={format.id}>
                        {format.label}
                      </NativeSelectOption>
                    ))}
                  </NativeSelect>
                </Field>
                <Field className="gap-2" data-invalid={problem("performedAt") ? true : undefined}>
                  <FieldLabel htmlFor={`${id}-done`}>Done</FieldLabel>
                  <Input
                    id={`${id}-done`}
                    name="performedAt"
                    type="datetime-local"
                    defaultValue={now}
                    aria-invalid={problem("performedAt") ? true : undefined}
                  />
                  {problem("performedAt") && <FieldError>{problem("performedAt")}</FieldError>}
                </Field>
              </div>
              <Field className="gap-2">
                <FieldLabel htmlFor={`${id}-description`}>Description</FieldLabel>
                <Textarea id={`${id}-description`} name="description" rows={3} maxLength={10_000} />
              </Field>
            </FieldGroup>
          </CardContent>
        </Card>

        <DraftParts
          parts={parts}
          onChange={setParts}
          options={options}
          says="How each part was scored cannot change once it is logged; its result, level, note and feel can."
          check={(sheet, index) => readLoggedParts(sheet, zone, options, index)}
          describe={(fields, index) => <LoggedPart fields={fields} index={index} zone={zone} options={options} />}
          result={(initial, index) => (types, timeLimit) => (
            <PartResult label={partLabel(index)} types={types} timeLimit={timeLimit} initial={initial} />
          )}
        />

        {/* No toast once it is logged: its own page opens, showing it. */}
        <div className="flex justify-end gap-2">
          <Button asChild variant="outline" size="lg">
            <Link to="/t/me">Cancel</Link>
          </Button>
          <Button type="submit" size="lg" disabled={parts.length === 0 || navigation.state === "submitting"}>
            Log workout
          </Button>
        </div>
        <LeaveGuard form={form} title="Leave without logging the workout?">
          What you typed here is not saved.
        </LeaveGuard>
      </Form>
    </section>
  );
}

/**
 * A part kept on the form, as its card reads it: its title, how it is
 * scored, and how it went.
 */
function LoggedPart({
  fields,
  index,
  zone,
  options,
}: {
  fields: PartFields;
  index: number;
  zone: string;
  options: EventOptions;
}) {
  const part = readLoggedParts(formOf(fields), zone, options, index).value?.[0];
  if (!part) return null;
  const types = part.scores.map(({ scoreTypeId }) => options.scoreTypes.find((type) => type.id === scoreTypeId));
  const shown = (part.results ?? []).map((score, position) =>
    scoreDisplay({ ...score, capped: score.capped ?? false, measure: types[position]?.measure ?? "" }),
  );
  const [first = "", ...tiebreaks] = shown;
  const went = [
    first || (part.results ? "not recorded" : ""),
    ...tiebreaks.filter(Boolean).map((tiebreak) => `then ${tiebreak}`),
    part.level ? levelLabel(part.level, part.rxPlus ?? false) : "",
    feelEmoji(part.feel),
  ].filter(Boolean);
  return (
    <div className="min-w-0 space-y-1">
      <p className="font-heading text-lg font-semibold leading-snug">
        {partLabel(index)}. {part.title}
      </p>
      <p className="text-sm text-muted-foreground">{partSummary(part.scores, part.timeLimitMs, options)}</p>
      {part.description && <p className="whitespace-pre-line pt-1 text-sm">{part.description}</p>}
      {went.length > 0 && <p className="pt-1 text-sm font-medium">{went.join(" · ")}</p>}
    </div>
  );
}

/**
 * A part's result, typed under how it is scored: a box per number for each
 * score type chosen, a level — or none, which leaves the part without levels
 * — and a note and a feel. A time can hit the cap whenever the part may have
 * one; the api checks it against the cap typed. Opened again, it starts from
 * what its sheet sent.
 */
function PartResult({
  label,
  types,
  timeLimit,
  initial,
}: {
  label: string;
  types: ScoreTypeOption[];
  timeLimit: "required" | "optional" | "none";
  initial: PartFields | undefined;
}) {
  const prefix = SHEET_PREFIX;
  const typed = (name: string) => initial?.find(([field]) => field === name)?.[1];
  const scored = types[0] !== undefined && types[0].measure !== "none";
  // Which level is picked, so the Rx+ badge is offered at Rx alone; none
  // leaves the part without levels.
  const [level, setLevel] = useState<Level>(() => (typed("level") ?? "rx") as Level);
  return (
    <div role="group" aria-label={`Part ${label}, how it went`} className="space-y-4 rounded-2xl border border-border p-4">
      <p className="text-sm font-medium text-foreground">How it went</p>
      {scored &&
        types.map((type, position) => (
          // Keyed by the type, so choosing another resets its boxes.
          <div key={`${position}-${type.id}`} className="space-y-2">
            {types.length > 1 && (
              <p className="text-sm text-muted-foreground">{position === 0 ? type.label : `Then ${type.label.toLowerCase()} (optional)`}</p>
            )}
            <ScoreBoxes
              prefix={`${prefix}.results.${position}`}
              type={type}
              position={position}
              timeLimitMs={timeLimit === "none" ? null : 1}
              current={typedScore(initial, position, type)}
            />
          </div>
        ))}
      {scored && (
        <div className="flex flex-wrap items-end gap-3">
          {/* Segments where there were radios, posting `level` as they did: "" for no level. */}
          <div className="min-w-56 flex-1">
            <SegmentedControl<Level>
              label={`Part ${label} level`}
              name={`${prefix}.level`}
              options={[...LEVELS.map((option) => ({ value: option.value, label: option.label })), { value: "", label: "No level" }]}
              value={level}
              onChange={setLevel}
            />
          </div>
          {level === "rx" && <RxPlusToggle name={`${prefix}.rxPlus`} on={typed("rxPlus") !== undefined} />}
        </div>
      )}
      <NoteFields
        prefix={prefix}
        label={label}
        current={initial && { feel: typed("feel") || null, note: typed("note") || null }}
      />
    </div>
  );
}

/**
 * The score a part's sheet sent at `position`, for its boxes to start from —
 * while its type is still the one it was typed for.
 */
function typedScore(initial: PartFields | undefined, position: number, type: ScoreTypeOption): ResultScore | undefined {
  if (!initial?.some(([name, value]) => name === `scores.${position}.type` && value === type.id)) return undefined;
  const read = readScore(formOf(initial), `${SHEET_PREFIX}.results.${position}`, type, "").value;
  if (!read) return undefined;
  return {
    scoreTypeId: type.id,
    label: type.label,
    measure: type.measure,
    better: type.better,
    // A capped time's value is the cap, which the api fills in; the boxes
    // read only that it hit it, and the reps.
    value: read.value ?? (read.capped ? 0 : null),
    reps: read.reps ?? null,
    capped: read.capped ?? false,
    unit: read.unit ?? null,
    display: "",
  };
}
